import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { DatePicker } from "@/components/common/DatePicker";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader as AlertHeader,
  AlertDialogFooter, AlertDialogTitle as AlertTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { DEPARTMENTS, TASK_STATUSES } from "@/constants";
import { Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export const PhaseModal = ({ phase, onClose, onSaved, onDeleted, canEditDates = true }) => {
  const [form, setForm] = useState({
    name: phase.name || "", department: phase.department || "", status: phase.status || "Not Started",
    start_date: phase.start_date || "", target_end_date: phase.target_end_date || "",
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/phases/${phase.id}`, {
        ...form, start_date: form.start_date || null, target_end_date: form.target_end_date || null,
      });
      toast.success("Phase updated");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/phases/${phase.id}`);
      toast.success("Phase deleted");
      onDeleted?.();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="phase-modal" className="max-w-md">
        <DialogHeader><DialogTitle>Edit Phase</DialogTitle></DialogHeader>
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <Label className="text-xs font-semibold">Phase Name</Label>
            <Input data-testid="phase-modal-name-input" required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} style={{ borderRadius: "2px" }} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold">Department</Label>
              <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                <SelectTrigger data-testid="phase-modal-department-select" style={{ borderRadius: "2px" }}><SelectValue /></SelectTrigger>
                <SelectContent>{DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="phase-modal-status-select" style={{ borderRadius: "2px" }}><SelectValue /></SelectTrigger>
                <SelectContent>{TASK_STATUSES.filter((s) => s !== "Overdue").map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold">Start Date</Label>
              <DatePicker testId="phase-modal-start-date-input" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} disabled={!canEditDates} />
            </div>
            <div>
              <Label className="text-xs font-semibold">Target End Date</Label>
              <DatePicker testId="phase-modal-end-date-input" value={form.target_end_date} onChange={(v) => setForm({ ...form, target_end_date: v })} disabled={!canEditDates} />
            </div>
          </div>
          {!canEditDates && (
            <p className="text-[11px] text-muted-foreground font-mono" data-testid="phase-dates-locked-hint">&gt; timeline locked — only the project creator can change start/end dates</p>
          )}
          <div className="flex items-center justify-between pt-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button type="button" data-testid="phase-modal-delete-btn" className="text-destructive text-xs font-semibold flex items-center gap-1 hover:underline">
                  <Trash size={14} />Delete Phase
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertHeader>
                  <AlertTitle>Delete this phase?</AlertTitle>
                  <AlertDialogDescription>Tasks in this phase will become unassigned to any phase. This cannot be undone.</AlertDialogDescription>
                </AlertHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction data-testid="phase-modal-confirm-delete-btn" onClick={handleDelete}>Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <div className="flex gap-2">
              <button type="button" data-testid="phase-modal-cancel-btn" onClick={onClose} className="btn-outline-navy">Cancel</button>
              <button type="submit" data-testid="phase-modal-save-btn" disabled={saving} className="btn-navy">{saving ? "Saving..." : "Save Phase"}</button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
